// src/templates/StartupFounder.jsx
import React from "react";
import data from "../data/startupFounder.json";
import { motion } from "framer-motion";
import { Link } from "react-router-dom"; // Make sure to place this in /src/assets or /public if needed

const StartupFounder = () => {
  const {
    name = "",
    logo = "",
    bio = "",
    services = [],
    vision = "",
    social = [],
    contact = "",
    projects = [],
    techStack = [],
  } = data || {};

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100 text-[#111827] min-h-screen font-sans px-4">
      <div className="absolute top-6 left-6 z-20 flex items-center gap-3">
        <Link to="/templates" className="flex items-center space-x-3">
        <img src="/logo.png" alt="Project Port Logo" className="h-12 w-12 drop-shadow-lg" />
        <span className="text-xl font-bold text-black">Project Port</span>
        </Link>
      </div>
      {/* Floating Particles */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
        <div className="animate-float w-32 h-32 bg-indigo-300 opacity-10 rounded-full absolute top-10 left-10 blur-2xl"></div>
        <div className="animate-float w-40 h-40 bg-purple-300 opacity-10 rounded-full absolute bottom-20 right-10 blur-2xl"></div>
        <div className="animate-float w-24 h-24 bg-pink-300 opacity-10 rounded-full absolute top-1/2 left-1/3 blur-2xl"></div>
      </div>

      {/* Content Wrapper */}
      <div className="relative z-10">
        {/* Header */}
        <motion.header
          className="flex flex-col items-center justify-center py-16 text-center"
          initial={{ opacity: 0, y: -60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          {logo && (
            <motion.img
              src={logo}
              alt="Founder Logo"
              className="w-32 h-32 md:w-40 md:h-40 mb-6 rounded-full border-4 border-indigo-500 object-cover shadow-xl"
              whileHover={{ scale: 1.05 }}
            />
          )}
          <h1 className="text-4xl md:text-5xl font-extrabold text-indigo-600 mb-3">{name}</h1>
          <p className="text-[#4b5563] max-w-2xl">{bio}</p>
        </motion.header>

        {/* Services */}
        <section className="py-12 max-w-6xl mx-auto">
          <motion.h2
            className="text-3xl font-bold text-indigo-700 mb-8 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            What I Do
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-xl shadow-md border hover:shadow-xl transition duration-300"
                whileHover={{ scale: 1.05 }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.15 }}
              >
                <h3 className="text-xl font-semibold mb-2 text-indigo-600">{service.title}</h3>
                <p className="text-[#4b5563]">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Projects */}
        <section className="py-12 max-w-6xl mx-auto">
          <motion.h2
            className="text-3xl font-bold text-indigo-700 mb-8 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            Featured Projects
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((project, i) => (
              <motion.div
                key={i}
                className="bg-white p-6 rounded-xl border shadow hover:shadow-lg transition"
                whileHover={{ scale: 1.02 }}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.2 }}
              >
                <h3 className="text-xl font-semibold text-indigo-600 mb-2">{project.title}</h3>
                <p className="text-[#4b5563] mb-2">{project.description}</p>
                <a
                  href={project.link}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-indigo-600 hover:underline"
                >
                  View Project ↗
                </a>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Tech Stack */}
        <section className="py-10 max-w-4xl mx-auto text-center">
          <motion.h2
            className="text-2xl font-bold text-indigo-700 mb-6"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            Tech Stack
          </motion.h2>
          <div className="flex flex-wrap justify-center gap-6">
            {techStack.map((tech, i) => (
              <motion.img
                key={i}
                src={tech.logo}
                alt={tech.name}
                className="w-12 h-12 hover:scale-110 transition"
                title={tech.name}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: i * 0.1 }}
              />
            ))}
          </div>
        </section>

        {/* Vision */}
        <section className="py-12 max-w-3xl mx-auto text-center">
          <motion.h2
            className="text-3xl font-bold text-indigo-700 mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            My Vision
          </motion.h2>
          <motion.p
            className="text-[#4b5563] leading-relaxed"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {vision}
          </motion.p>
        </section>

        {/* Contact */}
        <section className="py-10 max-w-3xl mx-auto text-center">
          <motion.h2
            className="text-2xl font-bold text-indigo-700 mb-2"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            Let’s Connect
          </motion.h2>
          <p className="text-[#4b5563] mb-4">Have a project or idea? Reach out:</p>
          <a
            href={`mailto:${contact}`}
            className="text-white px-6 py-2 bg-indigo-500 rounded-full hover:bg-indigo-600 transition shadow-md"
          >
            {contact}
          </a>
        </section>

        {/* Footer / Social */}
        <footer className="text-center py-8 text-sm text-gray-600">
          <div className="flex justify-center gap-6 mb-4">
            {social.map((link, i) => (
              <motion.a
                key={i}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:underline"
                whileHover={{ scale: 1.1 }}
              >
                {link.platform}
              </motion.a>
            ))}
          </div>
          <p>© {new Date().getFullYear()} {name}</p>
        </footer>
      </div>

      {/* Tailwind animation */}
      <style>{`
        .animate-float {
          animation: float 10s ease-in-out infinite;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) }
          50% { transform: translateY(-20px) }
        }
      `}</style>
    </div>
  );
};

export default StartupFounder;
